import AccountCard from "@/components/features/account/account-card/accountCard";
import {
  Box,
  Button,
  Drawer,
  Flex,
  Group,
  Image,
  Modal,
  rem,
  Select,
  Space,
  Stack,
  Text,
  TextInput,
} from "@mantine/core";
import { DateInput } from "@mantine/dates";
import { IconCalendar } from "@tabler/icons-react";
import ImgArrows from "@/assets/images/pngs/arrows.png";
import ImgNotiFail from "@/assets/images/pngs/noti-fail.png";
import { UseFormReturnType } from "@mantine/form";
import { ChangeEvent, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

interface Props {
  opened: boolean;
  onClose: () => void;
  onSubmit: (value: any) => void;
  form: UseFormReturnType<any>;
  title?: string;
  description?: string;
}
const MAX_DATE_RANGE_DAYS = 90;

const FilterStatementDrawer = ({
  opened,
  onClose,
  onSubmit,
  form,
  title,
  description,
}: Props) => {
  const { t } = useTranslation();

  const [dateRangeModalOpen, setDateRangeModalOpen] = useState(false);

  const toDateRef = useRef<HTMLInputElement>(null);
  const fromDateRef = useRef<HTMLInputElement>(null);

  const checkDateRange = (from: Date | null, to: Date | null) => {
    if (from && to) {
      const diffDays = Math.abs(
        (to.getTime() - from.getTime()) / (1000 * 60 * 60 * 24)
      );
      if (diffDays > MAX_DATE_RANGE_DAYS) {
        setDateRangeModalOpen(true);
        return true;
      }
    }
    return false;
  };

  const submitForm = (values: any) => {
    const hasError = checkDateRange(values.fromDate, values.toDate);
    if (hasError) return;
    onSubmit(values);
  };

  useEffect(() => {
    if (!opened) {
      form.reset();
    }
  }, [opened]);

  const minAllowedDate = new Date();
  minAllowedDate.setDate(minAllowedDate.getDate() - 365);

  const handleFromDateChange = (value: Date | null) => {
    const { toDate } = form.values;
    if (value && toDate && value > toDate) {
      form.setValues({ fromDate: toDate, toDate: value });
      checkDateRange(toDate, value);
    } else {
      form.setFieldValue("fromDate", value);
      checkDateRange(value, toDate ?? null);
    }
  };

  const handleToDateChange = (value: Date | null) => {
    const { fromDate } = form.values;
    if (value && fromDate && value < fromDate) {
      form.setValues({ fromDate: value, toDate: fromDate });
      checkDateRange(value, fromDate);
    } else {
      form.setFieldValue("toDate", value);
      checkDateRange(fromDate ?? null, value);
    }
  };


  const formatAmount = (raw: string) => {
    if (!raw) return "";
    return raw.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  };

  const handleMinAmountChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.currentTarget.value.replace(/[^0-9,]/g, "").replace(/,/g, "");
    form.setFieldValue("minAmount", value);
  };

  const handleMaxAmountChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.currentTarget.value.replace(/[^0-9,]/g, "").replace(/,/g, "");
    form.setFieldValue("maxAmount", value);
  };

  const swapAmountsIfNeeded = () => {
    const { minAmount, maxAmount } = form.values;
    if (
      minAmount !== "" &&
      minAmount !== undefined &&
      maxAmount !== "" &&
      maxAmount !== undefined &&
      Number(minAmount) > Number(maxAmount)
    ) {
      form.setValues({ minAmount: maxAmount, maxAmount: minAmount });
    }
  };

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      title={t("TRANSACTION_HISTORY_FILTER")}
      withCloseButton
      position="right"
      size={548}
      styles={{
        title: { fontWeight: 500 },
        header: { borderBottom: "1px solid #e0e0e0" },
      }}
    >
      {/* Drawer content */}
      <form onSubmit={form.onSubmit(submitForm)}>
        <Space h={16} />

        <AccountCard title={title} description={description} />
        <Stack mt={20}>
          <Box>
            <Text fz={14}>{t("SELECT_TIME_RANGE")}</Text>
            <Flex gap={12} align="center">
              <DateInput
                ref={fromDateRef}
                placeholder={t("FROM_LABEL")}
                rightSection={
                  <IconCalendar
                    size={16}
                    onClick={() => fromDateRef.current?.focus()}
                  />
                }
                radius={8}
                valueFormat="DD/MM/YYYY"
                minDate={minAllowedDate}
                maxDate={new Date()}
                w="50%"
                {...form.getInputProps("fromDate")}
                onChange={handleFromDateChange}
              />
              <Box>
                <Image src={ImgArrows} alt="arrows" />
              </Box>
              <DateInput
                ref={toDateRef}
                placeholder={t("TO")}
                valueFormat="DD/MM/YYYY"
                minDate={minAllowedDate}
                maxDate={new Date()}
                rightSection={
                  <IconCalendar
                    size={16}
                    onClick={() => toDateRef.current?.focus()}
                  />
                }
                radius={8}
                w="50%"
                styles={{
                  rightSection: { fontSize: "12px", marginRight: "6px" },
                }}
                {...form.getInputProps("toDate")}
                onChange={handleToDateChange}
              />
            </Flex>
          </Box>

          <Box>
            <Text fz={14}>{t("TRANSACTION_VALUE")}</Text>
            <Flex gap={12} align="center">
              <TextInput
                placeholder={t("FROM_LABEL")}
                rightSection={<Text fz={12} color="dimmed">VND</Text>}
                radius={8}
                w="50%"
                inputMode="numeric"
                value={formatAmount(form.values.minAmount)}
                error={form.errors.minAmount}
                onChange={handleMinAmountChange}
                onBlur={swapAmountsIfNeeded}
                styles={{
                  rightSection: { fontSize: "12px", marginRight: "6px" },
                }}
              />
              <Box>
                <Image src={ImgArrows} alt="arrows" />
              </Box>
              <TextInput
                placeholder={t("TO")}
                rightSection={<Text fz={12} color="dimmed">VND</Text>}
                radius={8}
                w="50%"
                inputMode="numeric"
                value={formatAmount(form.values.maxAmount)}
                error={form.errors.maxAmount}
                onChange={handleMaxAmountChange}
                onBlur={swapAmountsIfNeeded}
                styles={{
                  rightSection: { fontSize: "12px", marginRight: "6px" },
                }}
              />
            </Flex>
          </Box>
          <Select
            data={[
              { label: t("CREDIT_AMOUNT_COL"), value: "CREDIT" },
              { label: t("DEBIT_AMOUNT_COL"), value: "DEBIT" },
            ]}
            label={t("CURRENCY_TYPE")}
            placeholder={t("SELECT_CURRENCY_TYPE")}
            radius={8}
            {...form.getInputProps("amountType")}
          />
        </Stack>

        <Group position="center" mt={rem(20)}>
          <Box
            style={{
              position: "fixed",
              bottom: 0,
              right: 0,
              width: "100%",
              padding: "10px 16px",
              display: "flex",
              justifyContent: "flex-end",
              gap: "10px",
              boxShadow: "0px 5px 15px 0px #00000026",
              height: "76px",
              alignItems: "center",
              backgroundColor: "white",
              zIndex: 1,
            }}
          >
            <Flex gap={10}>
              <Button
                onClick={() => { form.reset(); }}
                sx={() => ({
                  width: "100px",
                  backgroundColor: "#F4F4F6",
                  color: "#525866",
                  borderRadius: "8px",
                  padding: "8px 10px",
                  cursor: "pointer",
                  fontWeight: 400,
                  "&:hover": {
                    backgroundColor: "#F4F4F6",
                  },
                })}
              >
                {t("RESET")}
              </Button>
              <Button
                type="submit"
                sx={() => ({
                  width: "160px",
                  backgroundColor: "#2A81D0",
                  color: "white",
                  borderRadius: "8px",
                  padding: "8px 10px",
                  cursor: "pointer",
                  fontWeight: 400,
                })}
              >
                {t("SEARCH")}
              </Button>
            </Flex>
          </Box>
        </Group>
      </form>

      <Modal
        opened={dateRangeModalOpen}
        onClose={() => setDateRangeModalOpen(false)}
        withCloseButton={false}
        centered
        size="sm"
        zIndex={300}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            padding: "16px 8px 8px",
            textAlign: "center",
            gap: 12,
          }}
        >
          <img src={ImgNotiFail} alt="error" width={80} height={80} />
          <Text fw={600} fz={16}>
            {t("NOTICE")}
          </Text>
          <Text fz={14} color="dimmed">
            {t("PLEASE_SELECT_QUERY_PERIOD_WITHIN")} {MAX_DATE_RANGE_DAYS} {t("DAYS")}
          </Text>
          <Button
            fullWidth
            mt={4}
            sx={{
              backgroundColor: "#2A81D0",
              borderRadius: 8,
              "&:hover": { backgroundColor: "#1E679E" },
            }}
            onClick={() => setDateRangeModalOpen(false)}
          >
            {t("CLOSE")}
          </Button>
        </Box>
      </Modal>
    </Drawer>
  );
};
export default FilterStatementDrawer;
